import httpService from "./httpService";

const photoAlbumService = {

  async createPhotoAlbum(album: CreatePhotoAlbum) {
    const response = await httpService.post<PhotoAlbum>('/photo-album', album);
    if (album.coverPhoto) {
      await this.updatePhotoAlbumCover(album.id, album.coverPhoto);
    }
    return response.data;
  },

  async getPhotoAlbums(
    businessId: number,
    options?: {
      search?: string;
      sortBy?: 'title' | 'date' | 'photoCount';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PhotoAlbum[]> {
    const params = new URLSearchParams({ businessId: businessId.toString() });
    
    if (options?.search) {
      params.append('search', options.search);
    }
    if (options?.sortBy) {
      params.append('sortBy', options.sortBy);
    }
    if (options?.sortOrder) {
      params.append('sortOrder', options.sortOrder);
    }

    const { data } = await httpService.get<PhotoAlbum[]>(`/photo-album?${params.toString()}`);
    return data;
  },

  async getPhotoAlbumById(id: string, startIndex: number, endIndex: number): Promise<PhotoAlbumWithItems> {
    const { data } = await httpService.get<PhotoAlbumWithItems>(`/photo-album/album/${id}?start=${startIndex}&end=${endIndex}`);
    return data;
  },

  async deletePhotoAlbum(id: string): Promise<void> {
    await httpService.delete(`/photo-album/${id}`);
  },

  async updatePhotoAlbum(id: string, updatedAlbum: Partial<PhotoAlbum>): Promise<PhotoAlbum> {
    const { data } = await httpService.put<PhotoAlbum>(`/photo-album/${id}`, updatedAlbum);
    return data;
  },

  async updatePhotoAlbumCover(id: string, file: File): Promise<PhotoAlbumWithItems> {
    const formData = new FormData();
    formData.append('coverPhoto', file);

    const { data } = await httpService.put<PhotoAlbumWithItems>(`/photo-album/${id}/cover`, formData);
    return data;
  },

  async addPhotosToAlbum(albumId: string, photos: File[]): Promise<Photo[]> {
    const formData = new FormData();
    photos.forEach(photo => formData.append('photos', photo));

    const { data } = await httpService.post<Photo[]>(`/photo-album/${albumId}/photos`, formData);
    return data;
  },

  async deletePhoto(albumId: string, photoId: number): Promise<void> {
    await httpService.delete(`/photo-album/${albumId}/photos/${photoId}`);
  },

  async getPhotoAlbumZip(albumId: string): Promise<Blob> {
    const response = await httpService.get(`/photo-album/${albumId}/zip`, {
      responseType: 'blob'
    });
    
    if (response.status !== 200) {
      throw new Error('Failed to download album zip');
    }
    
    return response.data;
  },

  async getTotalItemCountForBusiness(businessId: number): Promise<number> {
    const { data } = await httpService.get<number>(`/photo-album/count/${businessId}`);
    return data;
  },

  async movePhoto(albumId: string, photoId: number, targetAlbumId: string): Promise<void> {
    await httpService.put(`/photo-album/${albumId}/photos/${photoId}/move`, {
      targetAlbumId
    });
  },

  async deletePhotos(albumId: string, itemIds: number[]): Promise<void> {
    await httpService.delete(`/photo-album/${albumId}/photos`, {
      data: { itemIds }
    });
  },

  async movePhotos(albumId: string, itemIds: number[], targetAlbumId: string): Promise<void> {
    await httpService.put(`/photo-album/${albumId}/photos/move`, {
      itemIds,
      targetAlbumId
    });
  }

}

export default photoAlbumService;